import { useCallback, useEffect, useRef, useState } from 'react'
import { isMobile } from '../../../client'
import { prettyLog } from '../../../utils'

/** stop and drop the clip: frees the connection and, on iOS, the decoder */
const unload = (video: HTMLVideoElement) => {
  video.pause()
  video.removeAttribute('src')
  video.load()
}

/**
 * A card's muted preview clip (reels and Big Brother cards). It plays on
 * hover (desktop) or while `playing` (mobile: the card in focus, on screen).
 * Pausing unloads the clip, so playing again puts the source back first;
 * the card used to keep calling play() on a video with no source, and its
 * preview never came back.
 */
export const usePreviewVideo = (src: string, playing: boolean) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  // what the user wants now; a play() that resolves after a pause must not win
  const wantedRef = useRef(false)

  const play = useCallback(() => {
    const video = videoRef.current
    if (!video || wantedRef.current) {
      return
    }
    wantedRef.current = true
    if (!video.getAttribute('src')) {
      video.src = src
    }
    Promise.resolve(video.play())
      .then(() => {
        if (wantedRef.current) {
          setIsPlaying(true)
        } else if (videoRef.current === video) {
          unload(video)
        }
      })
      .catch((e: DOMException) => {
        wantedRef.current = false
        setIsPlaying(false)
        // a pause or a swipe interrupts play(); autoplay may be blocked
        if (e?.name !== 'AbortError' && e?.name !== 'NotAllowedError') {
          prettyLog('preview video', { error: e?.name, src }, 'error')
        }
      })
  }, [src])

  const pause = useCallback(() => {
    wantedRef.current = false
    setIsPlaying(false)
    const video = videoRef.current
    if (video && video.getAttribute('src')) {
      unload(video)
    }
  }, [])

  // mobile has no hover: the card in focus plays while the feed says so
  useEffect(() => {
    if (!isMobile()) {
      return
    }
    if (playing) {
      play()
    } else {
      pause()
    }
  }, [playing, play, pause])

  return { videoRef, isPlaying, play, pause }
}
